import { Phone, Mail, MessageSquare, Clock, Crown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import type { CsmTask, CsmTaskType } from '@/services/data/csmTasks';
import { differenceInCalendarDays, parseISO } from 'date-fns';

function getInitials(first?: string | null, last?: string | null): string {
  return `${first?.charAt(0) || ''}${last?.charAt(0) || ''}`.toUpperCase() || '?';
}

function getDeadlineColor(dueDate: string) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = differenceInCalendarDays(parseISO(dueDate), today);
  if (diff < 0) return 'border-red-500/40';
  if (diff === 0) return 'border-red-500/30';
  if (diff <= 2) return 'border-amber-500/30';
  return 'border-white/10';
}

function TaskTypeIcon({ type }: Readonly<{ type: CsmTaskType }>) {
  switch (type) {
    case 'call': return <Phone className="h-3.5 w-3.5" />;
    case 'email': return <Mail className="h-3.5 w-3.5" />;
    case 'sms': return <MessageSquare className="h-3.5 w-3.5" />;
    case 'operational': return <Clock className="h-3.5 w-3.5" />;
  }
}

const typeLabels: Record<CsmTaskType, string> = { call: 'Appel', email: 'Email', sms: 'SMS', operational: 'Opérationnel' };

interface CsmTaskCardProps {
  readonly task: CsmTask;
  readonly onClick?: () => void;
}

function getDeadlineLabel(diff: number): string {
  if (diff < 0) {
    return `J+${Math.abs(diff)}`;
  }
  if (diff === 0) {
    return "Aujourd'hui";
  }
  return `J-${diff}`;
}

function getDeadlineBadgeClass(diff: number): string {
  if (diff <= 0) {
    return 'text-red-400 border-red-500/30';
  }
  if (diff <= 2) {
    return 'text-amber-400 border-amber-500/30';
  }
  return 'text-muted-foreground border-white/10';
}

function getHealthScoreColor(healthScore: number): string {
  if (healthScore < 40) {
    return 'text-red-400';
  }
  if (healthScore < 70) {
    return 'text-amber-400';
  }
  return 'text-emerald-400';
}

export function CsmTaskCard({ task, onClick }: Readonly<CsmTaskCardProps>) {
  const deadlineClass = getDeadlineColor(task.due_date);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = differenceInCalendarDays(parseISO(task.due_date), today);
  const deadlineLabel = getDeadlineLabel(diff);
  const isPast = diff < 0;

  return (
    <button
      type="button"
      className={`group glass rounded-lg border p-3 transition-all cursor-pointer hover:border-amber-400/30 ${deadlineClass} ${isPast ? 'opacity-60' : ''} text-left w-full`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        {/* Client avatar */}
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarImage src={task.client_avatar_url || ''} />
          <AvatarFallback className="text-[10px] bg-white/10">{getInitials(task.client_first_name, task.client_last_name)}</AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          {/* Type badge + deadline */}
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-400/80">
              <TaskTypeIcon type={task.task_type} />
              <span>Tâche CSM · {typeLabels[task.task_type]}</span>
            </div>
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${getDeadlineBadgeClass(diff)}`}>
              {deadlineLabel}
            </Badge>
          </div>

          {/* Title */}
          <div className="text-sm font-medium text-foreground truncate">{task.title}</div>

          {/* Client name + meta */}
          <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              {task.client_first_name} {task.client_last_name}
              {task.client_is_vip && <Crown className="h-3 w-3 text-amber-400 fill-amber-400" />}
            </span>
            <span className="text-white/20">·</span>
            <span className="flex items-center gap-0.5"><Clock className="h-3 w-3" /> {task.estimated_minutes}m</span>
            {task.client_health_score !== null && task.client_health_score !== undefined && (
              <>
                <span className="text-white/20">·</span>
                <span className={getHealthScoreColor(task.client_health_score)}>
                  HS {task.client_health_score}
                </span>
              </>
            )}
          </div>

          {/* Phone quick action */}
          {task.client_phone && task.task_type === 'call' && (
            <a
              href={`tel:${task.client_phone}`}
              onClick={e => e.stopPropagation()}
              className="inline-flex items-center gap-1 mt-1.5 text-xs text-blue-400 hover:underline"
            >
              <Phone className="h-3 w-3" /> {task.client_phone}
            </a>
          )}

          {/* Status */}
          {task.status === 'in_progress' && (
            <div className="mt-1.5">
              <Badge variant="outline" className="text-[10px] bg-blue-500/20 text-blue-400 border-blue-500/30">En cours</Badge>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
